import { useCallback } from 'react';
import trim from 'lodash/trim';

import {
  InvoiceAvInvoiceBillingInfoContactEmail,
  InvoiceID,
  InvoiceUpdateAvInvoiceBillingInfoContactEmailCacheKeys
} from '../../../../../invoicesTypes';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';
import { useCacheInvoiceUpdateAvInvoiceBillingInfoContactEmail } from '../../../../../hooks/useCacheInvoiceUpdateAvInvoiceBillingInfoContactEmail';

import { INVOICE_UPDATE_AV_INVOICE_BILLING_INFO_CONTACT_EMAIL } from '../../../../../queries/invoiceUpdateAvInvoiceBillingInfoContactEmail.query';

import { InvoiceCache } from '../../../../../InvoiceCache';

import { isEmail } from '../../../../../../../utils/isEmail';

import { formsErrors } from '../../../../../../../locales/keys';

const emailRules = {
  required: formsErrors.email.required,
  validate: {
    isValid: (value: string) => isEmail(trim(value)) || formsErrors.email.valid
  }
};

const fieldName = 'email';

interface InvoiceContactEmailFormData {
  email: InvoiceAvInvoiceBillingInfoContactEmail;
}

interface UseInvoiceUpdateAvInvoiceBillingInfoContactEmailFormOptions {
  invoiceId: InvoiceID;
  contactEmail: InvoiceAvInvoiceBillingInfoContactEmail;
  cacheKeys?: InvoiceUpdateAvInvoiceBillingInfoContactEmailCacheKeys;
}

function useInvoiceUpdateAvInvoiceBillingInfoContactEmailForm({
  invoiceId,
  contactEmail,
  cacheKeys
}: UseInvoiceUpdateAvInvoiceBillingInfoContactEmailFormOptions) {
  const {
    invoiceUpdateAvInvoiceBillingInfoContactEmail,
    invoiceUpdateAvInvoiceBillingInfoContactEmailErrorMessage,
    invoiceUpdateAvInvoiceBillingInfoContactEmailLoading,
    invoiceUpdateAvInvoiceBillingInfoContactEmailReset
  } = useCacheInvoiceUpdateAvInvoiceBillingInfoContactEmail({
    query: INVOICE_UPDATE_AV_INVOICE_BILLING_INFO_CONTACT_EMAIL,
    cacheKeys: cacheKeys || [InvoiceCache.showCacheKey()]
  });

  const { handleSubmitReactHookForm, register, resetForm, errors } =
    useReactHookForm<InvoiceContactEmailFormData>({
      defaultValues: { email: contactEmail || '' },
      isModalForm: true
    });

  const resetFormWithCurrentEmail = useCallback(
    () => resetForm({ email: contactEmail || '' }),
    [resetForm, contactEmail]
  );

  return {
    validationErrors: errors?.email?.message,
    handleUpdateInvoiceContactEmail: handleSubmitReactHookForm({
      onSubmit: async (data) =>
        invoiceUpdateAvInvoiceBillingInfoContactEmail({
          uuid: invoiceId,
          contactEmail: trim(data.email)
        })
    }),
    updateInvoiceContactEmailReset:
      invoiceUpdateAvInvoiceBillingInfoContactEmailReset,
    resetForm: resetFormWithCurrentEmail,
    emailRegister: register(fieldName, emailRules),
    updateInvoiceContactEmailLoading:
      invoiceUpdateAvInvoiceBillingInfoContactEmailLoading,
    updateInvoiceContactEmailErrorMessage:
      invoiceUpdateAvInvoiceBillingInfoContactEmailErrorMessage
  };
}

export default useInvoiceUpdateAvInvoiceBillingInfoContactEmailForm;
